import { IABCollection } from "../global";
import { ArtblocksCollectionsDocument, ArtblocksCollectionDocument, execute } from "../.graphclient";
import { getScriptType } from "./utils";

const mutateCollection = (collection: IABCollection) => {
  // Flatten properties
  collection.contractAddress = collection.contract?.id || null;
  collection.mintingDate = collection.minterConfiguration?.startTime || null;
  collection.scriptLength = collection.script?.length || null;

  // Parse numbers
  collection.activatedAt = parseInt(collection.activatedAt) || null;
  collection.completedAt = parseInt(collection.completedAt) || null;
  collection.invocations = parseInt(collection.invocations) || null;
  collection.maxInvocations = parseInt(collection.maxInvocations) || null;
  collection.projectId = parseInt(collection.projectId);
  collection.mintingDate = parseInt(collection.mintingDate) || null;
  collection.updatedAt = parseInt(collection.updatedAt) || null;

  // Get script type
  collection.scriptType = getScriptType(collection);
};

export const getABCollection = async (id: string): Promise<IABCollection | null> => {
  try {
    // Normalize the ID: ensure contract address is lowercase
    // The ID format is: contractAddress-projectId
    // GraphQL subgraph stores IDs with lowercase contract addresses
    let normalizedId = id;
    if (id.includes('-')) {
      const [contractAddress, projectId] = id.split('-');
      // Normalize contract address to lowercase (subgraph stores IDs with lowercase addresses)
      normalizedId = `${contractAddress.toLowerCase()}-${projectId}`;
      
      // Only log if normalization changed the ID
      if (normalizedId !== id) {
        console.log(`[getABCollection] Normalized ID: ${id} -> ${normalizedId}`);
      }
    }
    
    console.log(`[getABCollection] Querying for project ID: ${normalizedId}`);
    const result = await execute(ArtblocksCollectionDocument, { id: normalizedId });
    
    if (!result?.data?.project) {
      console.error("No collection found for id:", normalizedId);
      // Log the full result to help debug
      if (result?.errors) {
        console.error("GraphQL errors:", result.errors);
      }
      if (result?.data) {
        console.error("GraphQL data:", result.data);
      }
      return null;
    }
    const collection = result.data.project as IABCollection;
    mutateCollection(collection);
    return collection;
  } catch (error) {
    console.error("Error fetching collection:", error);
    // Log more details about the error
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      if (error.stack) {
        console.error("Error stack:", error.stack);
      }
    }
    // Don't throw - return null instead so the UI can handle it gracefully
    return null;
  }
};

export const getABCollections = async (): Promise<Array<IABCollection>> => {
  try {
    const result = await execute(ArtblocksCollectionsDocument, {});
    const projects: Array<IABCollection> = result?.data?.projects || [];
    
    // DEBUG: Log contract addresses and V3 projects
    console.log('=== GraphQL Response Debug ===');
    console.log('Total projects fetched:', projects.length);
    
    const contractCounts = projects.reduce((acc, p) => {
      const contract = p.contract?.id?.toLowerCase() || 'unknown';
      acc[contract] = (acc[contract] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    console.log('Projects by contract:', contractCounts);
    
    const v3Contract = '0x99a9b7c1116f9ceeb1652de04d5969cce509b069';
    const v3Projects = projects.filter(p => 
      p.contract?.id?.toLowerCase() === v3Contract.toLowerCase()
    );
    console.log('V3 projects found:', v3Projects.length);
    
    if (v3Projects.length > 0) {
      console.log('Sample V3 projects:', v3Projects.slice(0, 5).map(p => ({
        projectId: p.projectId,
        name: p.name,
        contract: p.contract?.id,
        active: p.active,
        complete: p.complete,
        paused: p.paused,
        mintingDate: p.minterConfiguration?.startTime,
        activatedAt: p.activatedAt,
        invocations: p.invocations,
        maxInvocations: p.maxInvocations
      })));
      
      // Count V3 projects by status
      const v3Stats = {
        total: v3Projects.length,
        active: v3Projects.filter(p => p.active).length,
        complete: v3Projects.filter(p => p.complete).length,
        paused: v3Projects.filter(p => p.paused).length,
        incompleteAndActive: v3Projects.filter(p => !p.complete && p.active).length,
        wouldShowInAll: v3Projects.filter(p => p.complete || (!p.complete && p.active)).length
      };
      console.log('V3 projects status breakdown:', v3Stats);
    } else {
      console.warn('⚠️ No V3 projects found in response!');
      console.log('All contract addresses in response:', 
        Array.from(new Set(projects.map(p => p.contract?.id?.toLowerCase())))
      );
    }
    projects.forEach((collection) => {
      mutateCollection(collection);
      collection.script = null;
      collection.description = null;
    });

    const sortedProjects = [...projects].sort((a, b) => {
      const bDate = b.completedAt || b.mintingDate || b.activatedAt;
      const aDate = a.completedAt || a.mintingDate || a.activatedAt;
      return (bDate || 0) - (aDate || 0);
    });

    return sortedProjects;
  } catch (error) {
    console.error("Error fetching collections:", error);
    throw error;
  }
};
